import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Camera, X, FileText, Loader2 } from "lucide-react";
import { api, fileUrl, formatError } from "@/lib/api";

export const DOCUMENT_SLOTS = [
  { key: "photo", label: "Photo", mandatory: true, camera: true, accept: "image/*" },
  { key: "aadhaar", label: "Aadhaar Card", mandatory: true, camera: true, accept: "image/*,application/pdf" },
  { key: "artisan_card", label: "Artisan Card", mandatory: false, camera: true, accept: "image/*,application/pdf" },
  { key: "gst_certificate", label: "GST Certificate", mandatory: false, camera: true, accept: "image/*,application/pdf" },
  { key: "msme_certificate", label: "MSME Certificate", mandatory: false, camera: true, accept: "image/*,application/pdf" },
  { key: "cancelled_cheque", label: "Blank Cancelled Cash Memo / Receipt / Bill", mandatory: false, camera: true, accept: "image/*,application/pdf" },
  { key: "eligibility_proof", label: "Proof of GI Authorised User Eligibility (Training Certificate / Other)", mandatory: false, camera: true, accept: "image/*,application/pdf" },
  { key: "signature", label: "Upload your Clear Signature", mandatory: false, camera: true, accept: "image/*", hint: "Auto-inserted above the signature line in GI 3A and Statement of Case." },
];

export function DocumentsUploader({ value, onChange, testId = "docs" }) {
  const docs = value || {};
  const [busyKey, setBusyKey] = useState("");
  const fileRefs = useRef({});
  const cameraRefs = useRef({});

  const upload = async (slotKey, file) => {
    if (!file) return;
    setBusyKey(slotKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...docs, [slotKey]: { path: r.data.path, kind: r.data.kind || "image", name: file.name, uploaded_at: new Date().toISOString() } };
      onChange?.(next);
      toast.success(`${slotKey.replace(/_/g, " ")} uploaded`);
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusyKey(""); }
  };

  const remove = (slotKey) => onChange?.({ ...docs, [slotKey]: null });

  return (
    <section className="docs-uploader" data-testid={testId}>
      <header>
        <h3>Supporting documents</h3>
        <p>Attach clear scans or photos. You can also click a photo with your camera directly.</p>
      </header>
      <div className="docs-list">
        {DOCUMENT_SLOTS.map((slot) => {
          const cur = docs[slot.key];
          return (
            <div key={slot.key} className={`doc-slot ${cur ? "filled" : ""}`} data-testid={`doc-slot-${slot.key}`}>
              <div className="doc-slot-head">
                <div>
                  <b>{slot.label}</b>
                  {slot.mandatory && <span className="doc-mandatory">Mandatory</span>}
                  {slot.hint && <div className="doc-hint" style={{ fontSize: 11, color: "var(--muted)", marginTop: 3 }}>{slot.hint}</div>}
                </div>
                {cur && (
                  <button type="button" className="doc-remove" onClick={() => remove(slot.key)} data-testid={`doc-remove-${slot.key}`} aria-label={`Remove ${slot.label}`}><X size={13} /></button>
                )}
              </div>
              {cur ? (
                <div className="doc-preview">
                  {cur.kind === "image" ? (
                    <img src={fileUrl(cur.path)} alt={slot.label} data-testid={`doc-preview-${slot.key}`} />
                  ) : (
                    <a href={fileUrl(cur.path)} target="_blank" rel="noopener noreferrer" className="doc-file" data-testid={`doc-preview-${slot.key}`}>
                      <FileText size={20} /><span>{cur.name || "Document"}</span>
                    </a>
                  )}
                </div>
              ) : (
                <div className="doc-actions">
                  <label className="doc-btn upload" data-testid={`doc-upload-${slot.key}`}>
                    {busyKey === slot.key ? <Loader2 size={13} className="doc-spin" /> : <UploadCloud size={13} />}
                    {busyKey === slot.key ? "Uploading…" : "Upload file"}
                    <input
                      ref={el => (fileRefs.current[slot.key] = el)}
                      type="file"
                      accept={slot.accept}
                      onChange={e => { const f = e.target.files?.[0]; upload(slot.key, f); e.target.value = ""; }}
                      data-testid={`doc-file-input-${slot.key}`}
                    />
                  </label>
                  {slot.camera && (
                    <label className="doc-btn camera" data-testid={`doc-camera-${slot.key}`}>
                      <Camera size={13} /> Click photo
                      <input
                        ref={el => (cameraRefs.current[slot.key] = el)}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={e => { const f = e.target.files?.[0]; upload(slot.key, f); e.target.value = ""; }}
                        data-testid={`doc-camera-input-${slot.key}`}
                      />
                    </label>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function areMandatoryDocsFilled(docs) {
  return DOCUMENT_SLOTS.filter(s => s.mandatory).every(s => docs?.[s.key]?.path);
}
